import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerService } from '../../../Services/customer.service';
import { EquipmentService } from '../../../Services/equipment.service';
import { MaintenanceService } from '../../../Services/maintenance.service';
import { UserService } from '../../../Services/user.service';
import { Customer, CustomerHourlyRate, CustomerMaterialItem } from '../../../Models/customer.model';
import { Equipment } from '../../../Models/equipment.model';
import { Maintenance, MaintenanceStatus, MaintenanceRecurrenceType, User } from '../../../Models/maintenance.model';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { EquipmentFormComponent } from '../equipment-form/equipment-form.component';
import { MaintenanceFormComponent } from '../maintenance-form/maintenance-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../../api-authorization/authorize.service';

@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.css']
})
export class CustomerDetailComponent implements OnInit {
  customer: Customer | null = null;
  loading = true;
  activeTab = 0;
  users: User[] = [];
  private readonly tabStoragePrefix = 'wartung-customer-tab-';
  private readonly maxTabIndex = 3;
  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private allMaintenancesCache: Maintenance[] = [];
  private plannedMaintenancesCache: Maintenance[] = [];
  private serviceCallMaintenancesCache: Maintenance[] = [];
  private maintenancesWithMaterialsCache: Maintenance[] = [];
  private maintenanceCountByEquipmentId = new Map<number, number>();
  private lastMaintenanceDateByEquipmentId = new Map<number, Date>();
  private completionPercentCache = 0;
  private openMaintenanceCountCache = 0;
  private overdueMaintenanceCountCache = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private equipmentService: EquipmentService,
    private maintenanceService: MaintenanceService,
    private userService: UserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) {}

  /** Montage = nur lesen: Anlegen/Bearbeiten-Aktionen ausblenden. */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const tab = this.parseTabIndex(params.get('tab'));
      if (tab !== null) {
        this.activeTab = tab;
        this.persistActiveTab();
      }
    });

    this.route.params.subscribe(params => {
      const id = +params['id'];
      if (id) this.loadCustomer(id);
    });
    this.userService.getUsers().subscribe(u => this.users = u ?? []);
  }

  loadCustomer(id: number): void {
    this.loading = true;
    this.customerService.getCustomer(id).subscribe({
      next: (c) => {
        this.customer = c;
        this.rebuildMaintenanceCaches();
        const routeTab = this.parseTabIndex(this.route.snapshot.queryParamMap.get('tab'));
        this.activeTab = routeTab ?? this.getStoredTab(c.id) ?? this.activeTab;
        this.loading = false;
        this.handlePendingActionFromQuery();
      },
      error: () => {
        this.snackBar.open('Kunde nicht gefunden', 'OK', { duration: 4000 });
        this.router.navigate(['/wartung/kunden']);
      }
    });
  }

  reload(): void {
    if (this.customer) this.loadCustomer(this.customer.id);
  }

  /**
   * Wird die Seite mit ?action=plan|service geöffnet (z.B. aus den Schnellaktionen
   * im Dashboard), startet hier direkt der passende Dialog. Der Query-Parameter
   * wird danach entfernt, damit er beim Neuladen nicht erneut auslöst.
   */
  private handlePendingActionFromQuery(): void {
    const action = this.route.snapshot.queryParamMap.get('action');
    if (!action) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { action: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    setTimeout(() => {
      if (action === 'plan') {
        this.selectTab(1);
        this.addPlannedMaintenance();
      } else if (action === 'service') {
        this.selectTab(2);
        this.addServiceCall();
      }
    }, 0);
  }

  editCustomer(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: { ...this.customer }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  // --- Equipment / Verteiler ---
  get equipments(): Equipment[] {
    return this.customer?.equipments ?? [];
  }

  addEquipment(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(EquipmentFormComponent, {
      width: '480px',
      data: { customerId: this.customer.id }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  editEquipment(eq: Equipment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EquipmentFormComponent, {
      width: '480px',
      data: { ...eq, customerId: this.customer!.id }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  deleteEquipment(eq: Equipment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Verteiler löschen', message: `"${eq.name}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.equipmentService.deleteEquipment(eq.id).subscribe({
          next: () => { this.reload(); this.snackBar.open('Gelöscht', 'OK', { duration: 2000 }); },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  getMaintenanceCountForEquipment(eq: Equipment): number {
    return this.maintenanceCountByEquipmentId.get(eq.id) ?? 0;
  }

  getLastMaintenanceDate(eq: Equipment): Date | null {
    return this.lastMaintenanceDateByEquipmentId.get(eq.id) ?? null;
  }

  // --- Maintenances ---
  get allMaintenances(): Maintenance[] {
    return this.allMaintenancesCache;
  }

  get plannedMaintenances(): Maintenance[] {
    return this.plannedMaintenancesCache;
  }

  get serviceCallMaintenances(): Maintenance[] {
    return this.serviceCallMaintenancesCache;
  }

  get maintenancesWithMaterials(): Maintenance[] {
    return this.maintenancesWithMaterialsCache;
  }

  addPlannedMaintenance(): void {
    if (!this.customer || this.equipments.length === 0) {
      this.snackBar.open('Bitte zuerst einen Verteiler/Anlage anlegen', 'OK', { duration: 3000 });
      return;
    }
    const dialogRef = this.dialog.open(MaintenanceFormComponent, {
      width: '640px',
      data: { customerId: this.customer.id, customerName: this.customer.name, equipments: this.equipments, users: this.users, type: 'planned' }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  addServiceCall(): void {
    if (!this.customer) return;
    if (this.equipments.length === 0) {
      this.snackBar.open('Bitte zuerst einen Verteiler/Anlage anlegen', 'OK', { duration: 3000 });
      return;
    }
    // Navigate to the dedicated service call form
    this.persistActiveTab();
    this.router.navigate(['/wartung/einsatz-neu', this.customer.id]);
  }

  openMaintenance(m: Maintenance): void {
    this.persistActiveTab();
    this.router.navigate(['/wartung/auftrag', m.id], {
      queryParams: { customerTab: this.activeTab }
    });
  }

  selectTab(index: number): void {
    if (index === this.activeTab) return;

    this.activeTab = index;
    this.persistActiveTab();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: index },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  onTabChanged(index: number): void {
    this.selectTab(index);
  }

  onTabTouchStart(event: TouchEvent): void {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  onTabTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null || this.touchStartY === null) return;

    const touch = event.changedTouches[0];
    const diffX = touch.clientX - this.touchStartX;
    const diffY = touch.clientY - this.touchStartY;
    this.touchStartX = null;
    this.touchStartY = null;

    const isHorizontalSwipe = Math.abs(diffX) > 55 && Math.abs(diffX) > Math.abs(diffY) * 1.4;
    if (!isHorizontalSwipe) return;

    const nextTab = diffX < 0
      ? Math.min(this.maxTabIndex, this.activeTab + 1)
      : Math.max(0, this.activeTab - 1);

    if (nextTab !== this.activeTab) {
      this.selectTab(nextTab);
    }
  }

  onTabTouchCancel(): void {
    this.touchStartX = null;
    this.touchStartY = null;
  }

  private persistActiveTab(): void {
    if (!this.customer) return;
    try {
      sessionStorage.setItem(`${this.tabStoragePrefix}${this.customer.id}`, String(this.activeTab));
    } catch { /* ignore */ }
  }

  private getStoredTab(customerId: number): number | null {
    try {
      return this.parseTabIndex(sessionStorage.getItem(`${this.tabStoragePrefix}${customerId}`));
    } catch {
      return null;
    }
  }

  private parseTabIndex(value: string | null): number | null {
    if (value == null || value.trim() === '') return null;
    const tab = Number(value);
    return Number.isInteger(tab) && tab >= 0 && tab <= 3 ? tab : null;
  }

  private rebuildMaintenanceCaches(): void {
    const equipments = this.customer?.equipments ?? [];
    const all = equipments.flatMap(equipment =>
      (equipment.maintenances ?? []).map(maintenance => ({ ...maintenance, equipment }))
    );

    this.allMaintenancesCache = all;
    this.plannedMaintenancesCache = all
      .filter(m => m.recurrenceType !== MaintenanceRecurrenceType.None)
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
    this.serviceCallMaintenancesCache = all
      .filter(m => m.recurrenceType === MaintenanceRecurrenceType.None)
      .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime());
    this.maintenancesWithMaterialsCache = all.filter(m => (m.materials ?? []).length > 0);

    this.maintenanceCountByEquipmentId = new Map(
      equipments.map(equipment => [equipment.id, equipment.maintenances?.length ?? 0])
    );
    this.lastMaintenanceDateByEquipmentId = new Map();
    for (const equipment of equipments) {
      const lastCompleted = (equipment.maintenances ?? [])
        .filter(m => m.status === MaintenanceStatus.Completed && m.completedOn)
        .map(m => new Date(m.completedOn!))
        .sort((a, b) => b.getTime() - a.getTime())[0];
      if (lastCompleted) {
        this.lastMaintenanceDateByEquipmentId.set(equipment.id, lastCompleted);
      }
    }

    const done = all.filter(m => m.status === MaintenanceStatus.Completed).length;
    this.completionPercentCache = all.length === 0 ? 0 : Math.round((done / all.length) * 100);
    this.openMaintenanceCountCache = all.filter(m =>
      m.status !== MaintenanceStatus.Completed &&
      m.status !== MaintenanceStatus.Cancelled
    ).length;
    this.overdueMaintenanceCountCache = all.filter(m => m.status === MaintenanceStatus.Overdue).length;
  }

  deleteMaintenance(m: Maintenance, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Auftrag löschen', message: `"${m.title}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.maintenanceService.deleteMaintenance(m.id).subscribe({
          next: () => { this.reload(); this.snackBar.open('Gelöscht', 'OK', { duration: 2000 }); },
          error: () => this.snackBar.open('Fehler', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // --- Materials ---
  get materials(): CustomerMaterialItem[] {
    return this.customer?.materialItems ?? [];
  }

  newMaterial: Partial<CustomerMaterialItem> = { name: '', quantity: 1, unit: 'Stk', description: '' };
  addingMaterial = false;
  showMaterialForm = false;
  newHourlyRate: Partial<CustomerHourlyRate> = { label: '', hourlyRate: 130, isDefault: false, sortOrder: 0 };
  editingHourlyRateId: number | null = null;

  toggleMaterialForm(): void {
    this.showMaterialForm = !this.showMaterialForm;
    this.newMaterial = { name: '', quantity: 1, unit: 'Stk', description: '' };
  }

  saveMaterial(): void {
    if (!this.customer || !this.newMaterial.name?.trim()) return;
    this.addingMaterial = true;
    this.customerService.addMaterial(this.customer.id, {
      name: this.newMaterial.name!,
      quantity: this.newMaterial.quantity ?? 1,
      unit: this.newMaterial.unit ?? 'Stk',
      description: this.newMaterial.description,
      isPacked: false
    }).subscribe({
      next: () => {
        this.addingMaterial = false;
        this.showMaterialForm = false;
        this.reload();
      },
      error: () => { this.addingMaterial = false; }
    });
  }

  deleteMaterial(mat: CustomerMaterialItem): void {
    if (!this.customer) return;
    this.customerService.deleteMaterial(this.customer.id, mat.id).subscribe({
      next: () => this.reload(),
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  // --- Hourly rates ---
  get hourlyRates(): CustomerHourlyRate[] {
    return this.customer?.hourlyRates ?? [];
  }

  startEditHourlyRate(rate: CustomerHourlyRate): void {
    this.editingHourlyRateId = rate.id;
    this.newHourlyRate = { ...rate };
  }

  cancelHourlyRateEdit(): void {
    this.editingHourlyRateId = null;
    this.newHourlyRate = { label: '', hourlyRate: 130, isDefault: this.hourlyRates.length === 0, sortOrder: this.hourlyRates.length };
  }

  saveHourlyRate(): void {
    if (!this.customer || !this.newHourlyRate.label?.trim()) return;

    const payload = {
      label: this.newHourlyRate.label.trim(),
      hourlyRate: Number(this.newHourlyRate.hourlyRate ?? 0),
      isDefault: !!this.newHourlyRate.isDefault,
      sortOrder: Number(this.newHourlyRate.sortOrder ?? this.hourlyRates.length)
    };

    const request: any = this.editingHourlyRateId
      ? this.customerService.updateHourlyRate(this.customer.id, this.editingHourlyRateId, payload)
      : this.customerService.addHourlyRate(this.customer.id, payload);

    request.subscribe({
      next: () => {
        this.cancelHourlyRateEdit();
        this.reload();
        this.snackBar.open('Stundensatz gespeichert', 'OK', { duration: 2200 });
      },
      error: () => this.snackBar.open('Stundensatz konnte nicht gespeichert werden', 'OK', { duration: 3500 })
    });
  }

  deleteHourlyRate(rate: CustomerHourlyRate): void {
    if (!this.customer) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Stundensatz löschen', message: `"${rate.label}" wirklich löschen? Bestehende Einsätze behalten ihren gespeicherten Satz.` }
    });

    dialogRef.afterClosed().subscribe(r => {
      if (!r || !this.customer) return;
      this.customerService.deleteHourlyRate(this.customer.id, rate.id).subscribe({
        next: () => {
          this.reload();
          this.snackBar.open('Stundensatz gelöscht', 'OK', { duration: 2200 });
        },
        error: () => this.snackBar.open('Stundensatz konnte nicht gelöscht werden', 'OK', { duration: 3500 })
      });
    });
  }

  // --- Logo ---
  getLogoUrl(logoPath: string): string {
    return this.customerService.getLogoUrl(logoPath);
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.customer) return;
    const file = input.files[0];
    this.customerService.uploadLogo(this.customer.id, file).subscribe({
      next: () => {
        this.reload();
        this.snackBar.open('Logo hochgeladen', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Fehler beim Hochladen', 'OK', { duration: 4000 })
    });
    input.value = '';
  }

  removeLogo(): void {
    if (!this.customer) return;
    this.customerService.deleteLogo(this.customer.id).subscribe({
      next: () => {
        this.reload();
        this.snackBar.open('Logo entfernt', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 4000 })
    });
  }

  // --- Helpers ---
  getStatusClass(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'status-planned';
      case MaintenanceStatus.InProgress: return 'status-progress';
      case MaintenanceStatus.Completed: return 'status-completed';
      case MaintenanceStatus.Overdue: return 'status-overdue';
      case MaintenanceStatus.Cancelled: return 'status-cancelled';
      default: return '';
    }
  }

  getStatusLabel(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'Geplant';
      case MaintenanceStatus.InProgress: return 'In Arbeit';
      case MaintenanceStatus.Completed: return 'Erledigt';
      case MaintenanceStatus.Overdue: return 'Überfällig';
      case MaintenanceStatus.Cancelled: return 'Storniert';
      default: return status;
    }
  }

  getRecurrenceLabel(type: MaintenanceRecurrenceType): string {
    switch (type) {
      case MaintenanceRecurrenceType.Daily: return 'Täglich';
      case MaintenanceRecurrenceType.Weekly: return 'Wöchentlich';
      case MaintenanceRecurrenceType.Monthly: return 'Monatlich';
      case MaintenanceRecurrenceType.Quarterly: return 'Vierteljährlich';
      case MaintenanceRecurrenceType.Yearly: return 'Jährlich';
      default: return 'Einmalig';
    }
  }

  getCompletionPercent(): number {
    return this.completionPercentCache;
  }

  getCustomerInitial(customer: Customer): string {
    return customer.name?.trim().charAt(0).toUpperCase() || '?';
  }

  hasContactDetails(customer: Customer): boolean {
    return !!(
      customer.contactPersonName?.trim() ||
      customer.contactPersonPhone?.trim() ||
      customer.contactPersonEmail?.trim()
    );
  }

  getOpenMaintenanceCount(): number {
    return this.openMaintenanceCountCache;
  }

  getOverdueMaintenanceCount(): number {
    return this.overdueMaintenanceCountCache;
  }

  trackByEquipmentId(_: number, equipment: Equipment): number {
    return equipment.id;
  }

  trackByMaintenanceId(_: number, maintenance: Maintenance): number {
    return maintenance.id;
  }

  trackByCustomerMaterialId(_: number, material: CustomerMaterialItem): number {
    return material.id;
  }

  trackByMaintenanceMaterialId(_: number, material: { id?: number }): number | undefined {
    return material.id;
  }

  trackByHourlyRateId(_: number, rate: CustomerHourlyRate): number {
    return rate.id;
  }

  getTelLink(phone: string): string {
    const normalized = phone.replace(/[^\d+]/g, '');
    return `tel:${normalized}`;
  }

  getMailtoLink(email: string): string {
    return `mailto:${email.trim()}`;
  }

  getMapLink(address: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  getTechnicianNames(m: Maintenance): string {
    if (!m.assignedUsers?.length) return '–';
    return m.assignedUsers.map(u => u.userName).join(', ');
  }
}
